import React, { useEffect } from "react";
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { Helmet } from "react-helmet";
import care1 from "../../images/care1.jpeg";
import care2 from "../../images/care2.jpg";
import care3 from "../../images/care3.jpg";
import hero_section from "../../images/hero_section.jpeg";


function Services() {

  useEffect(() => {
    const revealElements = document.querySelectorAll(".fade-in, .slide-left, .slide-right");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    revealElements.forEach((element) => observer.observe(element));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="services-page">

      <Helmet>
        <title>Our Services | Home-Based Healthcare in Nairobi</title>
        <meta name="description" content="Explore the range of healthcare services offered by Wholistic Nursing Care, including personal care, geriatric support, and palliative care." />
        <meta name="keywords" content="nursing services Nairobi, palliative care, geriatric care, personal care assistance, home healthcare" />
        <link rel="canonical" href="https://wholistic.netlify.app/services" />
      </Helmet>
      
      {/* ✅ Page Header */}
      <div className="services-header fade-in">
        <h1>Our Services</h1>
        <p>At Wholistic Nursing Care, we believe in more than just assisting with daily activities 
           and giving prescribed medication. We prioritize holistic health—emotional, spiritual, 
           and mental wellness—because it all starts with the mind.</p>
      </div>

      {/* ✅ Personal Care Assistance */}
      <div className="service-section service-bg-light">
        <div className="service-text slide-left">
          <h2>Personal Care Assistance</h2>
          <p>
            Our caregivers provide essential assistance with daily living activities, 
            including bathing, grooming, dressing, and mobility support, ensuring dignity 
            and comfort at home. Suitable for patients who need continued healthcare at home,
             as it empowers a patient to gain independence.
          </p>
        </div>
        <div className="service-image-container slide-right">
        <img
          src={hero_section} 
          alt="Personal Care Assistance" 
          className="service-image" 
          effect="blur"
        />        </div>
      </div>

      {/* ✅ Medical Home Care */}
      <div className="service-section service-bg-dark">
        <div className="service-image-container slide-left">
        <LazyLoadImage 
          src={care3} 
          alt="Medical Home Care" 
          className="service-image" 
          effect="blur"
        />        </div>
        <div className="service-text slide-right">
          <h2>Medical Home Care</h2>
          <p>
          Home HealthCare is an option of care suitable for patients who need continued healthcare services
           at home, as it empowers a patient to gain independence. At wholistic we are giving clients the 
           option to gain independence while being around family which is a form of therapy compared to 
           hospital.Some of the illnesses that require home health care include;
            </p>
            <ul className="service-list">
              <li> Terminal illness such as critical care individuals who are in hospitals accumulating hospital bills
              </li>
              <li>Trauma such as road traffic accident,assault.
              </li>

              <li> Malignancies such as lymphomas and sickle cell
              </li>

              <li>Multiple diagnoses such as hypertension,diabetes,chronic lung disease,chronic heart disease as well
              </li>

              <li> Children born with disabilities. Down syndrome, cerebral palsy, chromosome 18. 
              </li>

        </ul>
        </div>
      </div>

      {/* ✅ Geriatric Care */}
      <div className="service-section service-bg-light">
        <div className="service-text slide-left">
          <h2>Geriatric Care</h2>
          <p>
            Geriatric care focuses on providing specialized healthcare for adults aged 65 and older
            who need support in managing age-related conditions.
          </p>

          <h3>Understanding Dementia & Alzheimer’s</h3>
          <ul>
          <li>
            Dementia  – a brain disorder that causes decline in cognitive functions, leading to memory loss, 
            communication difficulties, and impaired thinking.  
            
          </li>
          <li>Alzheimer’s disease – the most common cause of dementia, progressively damaging brain cells 
          and leading to cognitive decline.</li>
          </ul>

          <h3>How We Help</h3>
          <p>
            At Wholistic Home-Based Care, we deploy experienced registered nurses and nurse aides to 
            assist elderly patients with:
          </p>
          
          <ul className="service-list">
            <li>Grooming and personal hygiene.</li>
            <li>Supportive care to promote independence.</li>
            <li>Accompaniment to recreational activities.</li>
            <li>Emotional support and companionship services.</li>
            <li>Physical therapy and rehabilitation exercises.</li>
            <li>Mobility assistance and safety enhancements around the home.</li>

          </ul>
        </div>
        <div className="service-image-container slide-right">
        <LazyLoadImage 
          src={care2} 
          alt="Geriatric Care" 
          className="service-image" 
          effect="blur"
        />        </div>
      </div>


      {/* ✅ Hospice & Palliative Care */}
      <div className="service-section service-bg-dark">
        <div className="service-image-container slide-left">
          <img src={care1} alt="Hospice & Palliative Care" className="service-image" />
        </div>
        <div className="service-text slide-right">
          <h2>Hospice & Palliative Care</h2>

          <h3>Compassionate Care for End-of-Life & Chronic Illness</h3>
          <p>
            Hospice and palliative care provide non-curative treatments designed to improve comfort and quality of life
            for patients facing life-limiting illnesses.  
            Unlike hospitals, hospice is not a place, but rather a high-quality care approach that enables patients and families 
            to focus on living fully, despite a serious illness.
          </p>

          <h3>How It Works</h3>
          <p>
            Palliative care brings this holistic model of support earlier in the course of a serious illness.  
            Both hospice and palliative care programs include:
          </p>

          <ul className="service-list">
            <li>Nursing Care – Regular medical check-ups & symptom management.</li>
            <li>Pain Management – Ensuring comfort through medication & therapy.</li>
            <li>Spiritual Care & Support – Emotional & faith-based support.</li>
            <li>Physiotherapy, Occupational & Speech Therapy – Enhancing mobility & communication.</li>
            <li>Grief & Bereavement Counseling – Helping families cope with loss.</li>
            <li>Nutritional Counseling – Proper dietary guidance for well-being.</li>
            <li>Medical Equipment Supply – Providing essential home healthcare tools.</li>
          </ul>

          <p>
            Our interdisciplinary team ensures patients receive high-quality medical care, emotional support, and dignity
            in their final days.
          </p>
        </div>
      </div>

      
    </div>
  );
}

export default Services;
